import { useState } from 'react';
import type { Table, Wall } from '../types';

interface UseWallDrawingOptions {
  mode: 'wall' | 'delete' | null;
  tables: Table[];
  walls: Wall[];
  setWalls: React.Dispatch<React.SetStateAction<Wall[]>>;
  pushHistory: (nextTables: Table[], nextWalls: Wall[], description?: string) => void;
  saveScenario: (
    silent?: boolean,
    overrides?: {
      walls?: Wall[];
    },
    options?: {
      historyDescription?: string | null;
    },
  ) => void;
  gridRef: React.RefObject<HTMLDivElement>;
  uniqueId: () => string;
}

export function useWallDrawing({
  mode,
  tables,
  walls,
  setWalls,
  pushHistory,
  saveScenario,
  gridRef,
  uniqueId,
}: UseWallDrawingOptions) {
  const [isDrawing, setIsDrawing] = useState<boolean>(false);
  const [draftStart, setDraftStart] = useState<{ x: number; y: number } | null>(null);
  const [draftEnd, setDraftEnd] = useState<{ x: number; y: number } | null>(null);

  function getNormalizedCoords(e: React.PointerEvent<HTMLDivElement>): { x: number; y: number } {
    const rect = gridRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return {
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
    };
  }

  function handlePointerDown(e: React.PointerEvent<HTMLDivElement>): void {
    if (mode !== 'wall') return;
    e.stopPropagation();
    const coords = getNormalizedCoords(e);
    setIsDrawing(true);
    setDraftStart(coords);
    setDraftEnd(coords);
  }

  function handlePointerMove(e: React.PointerEvent<HTMLDivElement>): void {
    if (!isDrawing) return;
    e.preventDefault();
    e.stopPropagation();
    setDraftEnd(getNormalizedCoords(e));
  }

  function handlePointerUp(e: React.PointerEvent<HTMLDivElement>): void {
    if (!isDrawing || !draftStart) {
      setIsDrawing(false);
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    const endCoords = getNormalizedCoords(e);
    const dx = endCoords.x - draftStart.x;
    const dy = endCoords.y - draftStart.y;

    if (Math.abs(dx) > 0.001 || Math.abs(dy) > 0.001) {
      const newWall: Wall = {
        id: uniqueId(),
        x1: draftStart.x,
        y1: draftStart.y,
        x2: endCoords.x,
        y2: endCoords.y,
      };
      setWalls(prev => {
        const updated = [...prev, newWall];
        const description = 'Added wall';
        pushHistory(tables, updated, description);
        saveScenario(true, { walls: updated }, { historyDescription: description });
        return updated;
      });
    }

    setIsDrawing(false);
    setDraftStart(null);
    setDraftEnd(null);
  }

  function deleteWall(wallId: string): void {
    setWalls(prev => {
      const updated = prev.filter(wall => wall.id !== wallId);
      const description = 'Deleted wall';
      pushHistory(tables, updated, description);
      saveScenario(true, { walls: updated }, { historyDescription: description });
      return updated;
    });
  }

  return {
    isDrawing,
    draftStart,
    draftEnd,
    walls,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    deleteWall,
  };
}
